import { Autocomplete as MuiAutocomplete } from '@mui/material';
import { CSSProperties, useEffect, useRef } from 'react';
import { Text, TextInput } from '..';
import { colorSet } from '@theme';
import { XGray400Icon } from '@assets/iconComponents';

interface ITagSearchInputProps {
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  tags: string[];
  inputValue: string;
  options: string[];
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onInputChange: (event: React.SyntheticEvent, newValue: string) => void;
  onAutocompleteChange: (event: React.SyntheticEvent, value: string | null) => void;
  onTagClear: (tag: string) => void;
  boxstyle?: CSSProperties;
}

// 피그마 'Text input' 컴포넌트 + 오토컴플리트 기능 제공 + SearchInput 내부에 생성된 태그 보여주기 기능 제공
export const TagSearchInput = (props: ITagSearchInputProps) => {
  const {
    label,
    placeholder,
    disabled = false,
    inputValue,
    options,
    tags,
    onKeyDown,
    onInputChange,
    onAutocompleteChange,
    onTagClear,
    boxstyle
  } = props;

  const tagContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tagContainerRef.current) {
      tagContainerRef.current.scrollLeft = tagContainerRef.current.scrollWidth;
    }
  }, [tags]);

  return (
    <div style={boxstyle}>
      {label ? (
        <Text variant="subtitle" fontWeight="bold" marginBottom="8px">
          {label}
        </Text>
      ) : null}

      <MuiAutocomplete
        className="search-input-autocomplete" // theme.ts MuiAutocomplete style 적용을 위한 className 부여
        disabled={disabled}
        open={options && options.length > 0}
        inputValue={inputValue ?? ''}
        options={options}
        onKeyDown={onKeyDown}
        onInputChange={(event, newValue) => {
          onInputChange(event, newValue);
        }}
        value={null} //inputValue와 options 요소가 일치할 시 onChange가 실행되지 않는 이슈 픽스
        onChange={(event, newValue, reason) => {
          //options 리스트에서 키워드를 선택했을 시에만 실행
          if (reason === 'selectOption' && newValue && options.includes(newValue)) {
            onAutocompleteChange(event, newValue);
          }
        }}
        isOptionEqualToValue={(option, value) => option === value} //inputValue와 options 요소가 일치할 시 onChange가 실행되지 않는 이슈 픽스
        renderInput={(params) => {
          return (
            <TextInput
              {...params}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <div
                    ref={tagContainerRef}
                    style={{
                      display: 'flex',
                      overflowX: 'auto',
                      maxWidth: '400px',
                      whiteSpace: 'nowrap',
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none'
                    }}
                  >
                    {tags && tags.length > 0
                      ? tags.map((tag) => (
                          <div
                            key={tag}
                            style={{
                              height: 28,
                              border: `1px solid ${colorSet.gray300}`,
                              borderRadius: '6px',
                              padding: '0px 10px',
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              marginRight: '10px',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <Text variant="body1" marginRight="2px">
                              {tag}
                            </Text>
                            <XGray400Icon
                              onClick={() => onTagClear(tag)}
                              style={{ width: 16, height: 16, cursor: 'pointer' }}
                            />
                          </div>
                        ))
                      : null}
                  </div>
                )
              }}
              placeholder={tags.length > 0 ? '' : (placeholder ?? 'Search')}
            />
          );
        }}
        clearIcon={false} // 값 입력시 보여지는 X 버튼을 보이지 않게 처리
      />
    </div>
  );
};
